import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IntegrationCard = ({ integration, onConnect, onDisconnect, onConfigure }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success';
      case 'error':
        return 'text-destructive';
      case 'pending':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return 'CheckCircle';
      case 'error':
        return 'XCircle';
      case 'pending':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div
      className={`relative bg-card border border-border rounded-xl p-6 transition-all duration-300 hover:shadow-lg ${
        isHovered && integration?.status === 'connected' ? 'shadow-primary/20' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glow effect for connected integrations */}
      {integration?.status === 'connected' && isHovered && (
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl animate-pulse-soft" />
      )}
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${integration?.bgColor}`}>
              <Icon 
                name={integration?.icon} 
                size={24} 
                className={integration?.iconColor}
              />
            </div>
            <div>
              <h3 className="font-semibold text-card-foreground">{integration?.name}</h3>
              <p className="text-sm text-muted-foreground">{integration?.category}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon 
              name={getStatusIcon(integration?.status)} 
              size={16} 
              className={getStatusColor(integration?.status)}
            />
            <span className={`text-sm font-medium capitalize ${getStatusColor(integration?.status)}`}>
              {integration?.status}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {integration?.description}
        </p>

        {/* Connection Details */}
        {integration?.status === 'connected' && (
          <div className="bg-muted rounded-lg p-3 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Last Sync:</span>
                <p className="font-medium text-card-foreground">
                  {formatLastSync(integration?.lastSync)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Data Points:</span>
                <p className="font-medium text-card-foreground">
                  {integration?.dataPoints?.toLocaleString() || '0'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {integration?.status === 'error' && integration?.errorMessage && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-destructive mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">Connection Error</p>
                <p className="text-xs text-destructive/80 mt-1">{integration?.errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {integration?.status === 'connected' ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onConfigure(integration)}
                  iconName="Settings"
                  iconPosition="left"
                >
                  Configure
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDisconnect(integration)}
                  iconName="Unplug"
                  iconPosition="left"
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => onConnect(integration)}
                iconName="Plug"
                iconPosition="left"
                loading={integration?.status === 'pending'}
              >
                {integration?.status === 'pending' ? 'Connecting...' : 'Connect'}
              </Button>
            )}
          </div>

          {integration?.status === 'connected' && (
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse-soft" />
              <span className="text-xs text-muted-foreground">Live</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegrationCard;